import React, { Component } from 'react';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { some } from 'lodash';

import { Button } from 'react-bootstrap';

import { AppConstants } from '../../../constants';
import { ILoanAccount } from '../../../interfaces';
import { LoanAccountActivityStore, OptionsStoreClass } from '../../../stores';
import { DropdownFilter, Icon, Loader } from '../../common';

import ActivityList from './ActivityList';
import NewNote from './NewNote';

const { LOAN_ACCOUNT_ACTIVITY_TYPES } = AppConstants;

interface IProps {
  // Owned by LoanAccount rather than built here, so that the tabs alongside the feed can refresh it
  // after they write to it. c.f. the case page, where CaseStore holds the feed for the same reason.
  activityStore: LoanAccountActivityStore;
  loanAccount: ILoanAccount;
}

interface IInjected extends IProps {
  OptionsStore: OptionsStoreClass;
}

/**
 * The account's activity log, modeled on the case page's SidebarFeed. It has a single feed rather
 * than the case sidebar's Feed / Email / SMS tabs, so there is no nav to switch between.
 */
@inject('OptionsStore')
@autoBindMethods
@observer
class SidebarFeed extends Component<IProps> {
  @observable private showDeletedNotes = false;

  private get injected () {
    return this.props as IInjected;
  }

  private get activityStore () {
    return this.props.activityStore;
  }

  // Every type the feed can hold, labeled from the same /options/ list the rest of the lending UI
  // labels enums from rather than from a copy of the labels kept here.
  @computed
  private get filterableTypes () {
    return this.injected.OptionsStore.loanAccountActivityTypeOptions
      .map(option => ({ id: option.value, name: option.name }));
  }

  // The feed is re-read for the type the note was written to, so a filter for anything else steps
  // aside rather than swallowing what the user just saved.
  private refreshForNotes () {
    return this.activityStore.refreshForType(LOAN_ACCOUNT_ACTIVITY_TYPES.NOTE);
  }

  private toggleShowDeletedNotes () {
    this.showDeletedNotes = !this.showDeletedNotes;
  }

  private handleFilterChange (change: { type?: string }) {
    this.activityStore.setTypeFilter(change.type || null);
  }

  private renderEmptyFeedMessage () {
    if (this.activityStore.loading || this.activityStore.activities.length) { return null; }

    // A filter that matches nothing is the user's own doing, so say that rather than telling them
    // the account has no activity at all.
    if (this.activityStore.typeFilter) {
      return <p className='empty-info'>No activity of this type on the account yet.</p>;
    }

    return (
      <p className='empty-info'>
        This is the account feed. <br />
        As loans are added and payments are recorded, they will show up here.
      </p>
    );
  }

  private renderTypeFilter () {
    return (
      <div className='filters dropdown-arrow'>
        <Icon type='filter' />
        <DropdownFilter
          bsStyle='link'
          defaultLabel='Filter'
          filterName='type'
          nullName='All'
          onFilterChange={this.handleFilterChange}
          options={this.filterableTypes}
          pullRight={false}
          selectedId={this.activityStore.typeFilter}
        />
      </div>
    );
  }

  private renderDeletedNotesFilter () {
    // Nothing to offer until the account has a deleted note to show
    if (!some(this.activityStore.activities, activity => !!activity.deleted_at)) { return null; }

    return (
      <div className='deleted'>
        <Button bsStyle='link' className='secondary btn-show-deleted-notes' onClick={this.toggleShowDeletedNotes}>
          <Icon type={this.showDeletedNotes ? 'check-square' : 'square-o'} />Show Deleted Notes
        </Button>
      </div>
    );
  }

  private renderLoadMore () {
    if (!this.activityStore.hasMore) { return null; }

    return (
      <Button
        className='load-more-activities'
        disabled={this.activityStore.loadingMore}
        onClick={this.activityStore.loadMore}
      >
        {this.activityStore.loadingMore ? <Loader className='button-loader' /> : 'Load More'}
      </Button>
    );
  }

  public render () {
    return (
      <aside>
        <div className='sidebar-feed'>
          <h2 className='feed-title'>Feed</h2>

          <NewNote
            loanAccountId={this.props.loanAccount.id}
            onNoteCreated={this.refreshForNotes}
          />

          <div className='wrp-filters'>
            {this.renderTypeFilter()}
            {this.renderDeletedNotesFilter()}
          </div>

          {this.renderEmptyFeedMessage()}

          <ActivityList
            activities={this.activityStore.activities}
            loading={this.activityStore.loading}
            onNoteDeleted={this.refreshForNotes}
            showDeletedNotes={this.showDeletedNotes}
          />

          {this.renderLoadMore()}
        </div>
      </aside>
    );
  }
}

export default SidebarFeed;
